/**
 * ================================================================================================
 *  FAVOURITES — ONE STORE, TWO PLACES THAT SHOW IT, AND NOW ONE ACCOUNT
 * ================================================================================================
 *
 * The approved customer kit stars a module in two places: on the card in `כל המודולים שלי`, and in
 * the `המועדפים שלי` row above it. Those are one fact shown twice, so they must come from one
 * store — a second copy inside each component would let the row and the card disagree about the
 * same star within a single render, and the person would be right to trust neither.
 *
 * ── IT WAS PER DEVICE AND SAID SO. 2026-11-01, SECTION 7. ─────────────────────────────────────
 *
 * This module's own header used to read: *"`localStorage`, because there is no
 * `user.preferences.update` among the route registry's 104 routes … Closing that needs a server
 * route and a column, which is a product change with its own decision."*
 *
 * That decision was taken in §6.9 and the home ORDER has travelled on the account ever since. The
 * star was the one personalisation left behind, which is exactly what the Owner named:
 * *"חבר זאת למנגנון ההתאמה האישית הקיים, בלי ליצור מנגנון נוסף"*, and *"הבחירה והסדר נשמרים לכל
 * משתמש ובין מכשירים"*.
 *
 * SO IT IS THE FOURTH PREFERENCE AND NOT A FOURTH MECHANISM. `preferences.ts` is still the one
 * file that knows both homes: this one names `'home.favourites'` and knows nothing about where it
 * is kept. The device key is UNCHANGED — `glpoint.favourites` — so nobody's existing stars are
 * orphaned by the move.
 *
 * ── AND IT STILL RENDERS WHEN NOTHING CAN BE STORED ──────────────────────────────────────────
 *
 * Every access goes through `readOnDevice`, which is wrapped: storage throws in a private window
 * and returns nothing with site data blocked, and a favourites list must never be the reason a
 * screen fails to render. The account push is fire-and-forget for the same reason it is for an
 * order — a refused write is not something to show somebody who just clicked a star.
 */

import { useCallback, useEffect, useState } from 'react';

import { deviceKeyFor, readOnDevice, rememberPreference } from './preferences';

/** The one preference this module names. Where it is kept is `preferences.ts`'s business. */
const KEY = 'home.favourites' as const;

/** Same-tab subscribers. `storage` fires in OTHER tabs only, so the writer tells this one itself. */
const listeners = new Set<(value: readonly string[]) => void>();

/**
 * `null` FROM THE DEVICE BECOMES `[]` HERE, and the distinction is not lost — it is simply not
 * this reader's to make. `hydratePreferences` is what reconciles *"this machine has no opinion"*
 * with the account's answer; by the time a screen asks, an empty list means no favourites.
 */
export function readFavourites(): readonly string[] {
  return readOnDevice(KEY) ?? [];
}

export interface Favourites {
  readonly favourites: readonly string[];
  readonly toggle: (id: string) => void;
}

export function useFavourites(): Favourites {
  const [favourites, setFavourites] = useState<readonly string[]>(() => readFavourites());

  useEffect(() => {
    const listener = (value: readonly string[]): void => setFavourites(value);
    listeners.add(listener);
    /*
     * A CHANGE MADE IN ANOTHER TAB of the same browser, which `storage` is exactly for. The key
     * compared is the DEVICE one, because that is what the browser event carries — and
     * `preferences.ts` owns the mapping, so this asks it rather than restating the string.
     */
    const onStorage = (event: StorageEvent): void => {
      if (event.key === null || event.key === deviceKeyFor(KEY)) setFavourites(readFavourites());
    };
    window.addEventListener('storage', onStorage);
    return () => {
      listeners.delete(listener);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  const toggle = useCallback((id: string): void => {
    const current = readFavourites();
    const next = current.includes(id) ? current.filter((x) => x !== id) : [...current, id];
    /* THE DEVICE FIRST AND THE ACCOUNT NEXT, which is `rememberPreference`'s own order. */
    rememberPreference(KEY, next);
    for (const listener of listeners) listener(next);
  }, []);

  return { favourites, toggle };
}

/**
 * A CHANGE THE ACCOUNT BROUGHT BACK, pushed in by whoever called `hydratePreferences`.
 *
 * `TenantHomeScreen` hydrates once at mount and is told WHICH keys changed; when `home.favourites`
 * is among them it calls this, and every subscriber re-reads. The alternative — an event this
 * module listened for — would have been a second notification mechanism beside the one the screen
 * already uses for the order and the hidden set.
 */
export function favouritesChangedOnAccount(): void {
  const value = readFavourites();
  for (const listener of listeners) listener(value);
}
